import React, { useState } from 'react';
import { ChevronDown, ArrowRight, ArrowLeft } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../hooks/useLanguage';
type CategoryKey = 'auditors' | 'advisors';
interface Person {
  id: string;
  nameKey: string;
  image: string;
  descriptionKey: string;
  category: CategoryKey;
}
const people: Person[] = [{
  id: 'john-smith-auditor',
  nameKey: 'johnSmith',
  image: 'https://cdn.magicpatterns.com/uploads/jUhmsjpJ7X6smQdUcGbpXD/Mask_Group_170.jpg',
  descriptionKey: 'roleDescription',
  category: 'auditors'
}, {
  id: 'jessica-doe-auditor',
  nameKey: 'jessicaDoe',
  image: 'https://cdn.magicpatterns.com/uploads/hsLEuikCCbQ1DNnvm6ALm8/Mask_Group_172.jpg',
  descriptionKey: 'roleDescription',
  category: 'auditors'
}, {
  id: 'rea-johnson-auditor',
  nameKey: 'reaJohnson',
  image: 'https://cdn.magicpatterns.com/uploads/rdgLQvk2TTEc4F7swVS2va/Mask_Group_171.jpg',
  descriptionKey: 'roleDescription',
  category: 'auditors'
}, {
  id: 'john-smith-advisor',
  nameKey: 'johnSmith',
  image: 'https://cdn.magicpatterns.com/uploads/jUhmsjpJ7X6smQdUcGbpXD/Mask_Group_170.jpg',
  descriptionKey: 'roleDescription',
  category: 'advisors'
}, {
  id: 'jessica-doe-advisor',
  nameKey: 'jessicaDoe',
  image: 'https://cdn.magicpatterns.com/uploads/hsLEuikCCbQ1DNnvm6ALm8/Mask_Group_172.jpg',
  descriptionKey: 'roleDescription',
  category: 'advisors'
}, {
  id: 'rea-johnson-advisor',
  nameKey: 'reaJohnson',
  image: 'https://cdn.magicpatterns.com/uploads/rdgLQvk2TTEc4F7swVS2va/Mask_Group_171.jpg',
  descriptionKey: 'roleDescription',
  category: 'advisors'
}];
const categoryKeys: CategoryKey[] = ['auditors', 'advisors'];
export function AdvisorsGrid() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('auditors');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const {
    t,
    direction
  } = useLanguage();
  const categoryLabels: Record<CategoryKey, string> = {
    auditors: t('advisorsAuditorsPage.grid.auditors'),
    advisors: t('advisorsAuditorsPage.grid.advisors')
  };
  const filteredPeople = people.filter(p => p.category === activeCategory);
  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;
  return <section ref={ref} className="py-20 bg-white relative z-10" aria-label="Advisors & Auditors Team" data-id="element-88">
      <div className="container mx-auto px-6 md:px-12" data-id="element-89">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-90">
          {/* Desktop Filters - Left Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0" data-id="element-91">
            <div className="sticky top-24 space-y-6" data-id="element-92">
              {categoryKeys.map((key, index) => <button key={key} onClick={() => setActiveCategory(key)} className={`w-full flex items-center justify-between group transition-all duration-300 ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#061D76] font-bold' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-93">
                  <div className="flex items-center gap-4" data-id="element-94">
                    <span className={`text-sm font-medium ${activeCategory === key ? 'text-[#061D76]' : 'text-[#25B8D6]'}`} data-id="element-95">
                      {index + 1}
                    </span>
                    <span className="text-lg" data-id="element-96">{categoryLabels[key]}</span>
                  </div>
                  <span className={`transition-opacity duration-300 ${activeCategory === key ? 'opacity-100' : 'opacity-0'}`} data-id="element-97">
                    <ArrowIcon className="w-5 h-5 text-[#061D76]" data-id="element-98" />
                  </span>
                </button>)}
            </div>
          </div>

          {/* Mobile Filter - Dropdown */}
          <div className="lg:hidden relative z-20" data-id="element-99">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full bg-white border border-gray-200 rounded-lg px-6 py-4 flex items-center justify-between text-[#061D76] font-bold text-lg shadow-sm" data-id="element-100">
              <span data-id="element-101">{categoryLabels[activeCategory]}</span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-102" />
            </button>

            {isDropdownOpen && <div className="absolute top-full left-0 right-0 w-full mt-2 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden z-30" data-id="element-103">
                {categoryKeys.map(key => <button key={key} onClick={() => {
              setActiveCategory(key);
              setIsDropdownOpen(false);
            }} className={`w-full px-6 py-3 hover:bg-gray-50 transition-colors ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#25B8D6] font-bold' : 'text-[#061D76]'}`} data-id="element-104">
                    {categoryLabels[key]}
                  </button>)}
              </div>}
          </div>

          {/* People Grid */}
          <div className="flex-1 min-h-[600px]" data-id="element-105">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-106">
              {filteredPeople.map((person, index) => <div key={person.id} className={`relative aspect-[3/4] overflow-hidden group reveal-hidden ${isVisible ? 'animate-fade-up' : ''}`} style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-107">
                  <img src={person.image} alt={t(`advisorsAuditorsPage.grid.${person.nameKey}`)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-108" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#061D76]/90 via-[#061D76]/40 to-transparent opacity-90" data-id="element-109" />
                  <div className="absolute bottom-0 left-0 w-full p-6 text-white text-center" data-id="element-110">
                    <h3 className="text-2xl font-bold mb-2" data-id="element-111">
                      {t(`advisorsAuditorsPage.grid.${person.nameKey}`)}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/90" data-id="element-112">
                      {t(`advisorsAuditorsPage.grid.${person.descriptionKey}`)}
                    </p>
                  </div>
                </div>)}
            </div>

            {filteredPeople.length === 0 && <div className="py-20 text-center text-gray-400" data-id="element-113">
                <p data-id="element-114">No members found in this category.</p>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}